import React, { useEffect, useRef, useState } from 'react';
import { IconCheck, IconPlus, IconTrash, IconUsersGroup, IconX } from '@tabler/icons-react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import type { Group } from '../../../entities/group';
import type { IntScorozvon } from '../../../entities/int-scorozvon';
import {
  fetchIntScorozvonMembers,
  saveIntScorozvon,
} from '../../../entities/int-scorozvon';
import { useAuth } from '../../../features/auth';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { Select } from '../../../shared/ui/Select';
import type { SelectOption } from '../../../shared/ui/Select';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import { type IntScorozvonForm, EMPTY_INT_SCOROZVON_FORM } from '../model/types';

interface IntScorozvonModalProps {
  mode: 'add' | 'edit';
  initial?: IntScorozvon | null;
  onClose: () => void;
  onSaved: () => void;
}

const IntScorozvonModal: React.FC<IntScorozvonModalProps> = ({ mode, initial, onClose, onSaved }) => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'ADMIN';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [originalMemberIds, setOriginalMemberIds] = useState<string[]>([]);
  const overlayRef = useRef<HTMLDivElement>(null);

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<IntScorozvonForm>({
    defaultValues:
      mode === 'edit' && initial
        ? {
            groupName: initial.groupName ?? '',
            managerId: initial.managerId ?? '',
            managerName: initial.expand?.managerId?.name ?? initial.expand?.managerId?.email ?? '',
            members: [],
          }
        : EMPTY_INT_SCOROZVON_FORM,
  });

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'members' });

  useEffect(() => {
    pb.collection('groups')
      .getFullList<Group>({ sort: 'name', fields: 'id,name,usersIds' })
      .then(setGroups)
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !initial) return;
    fetchIntScorozvonMembers(initial.id).then(members => {
      setOriginalMemberIds(members.map(m => m.id));
      replace(members.map(m => ({
        id: m.id,
        userId: m.userId,
        userName: m.expand?.userId?.name || m.expand?.userId?.email || '',
        number: m.number,
      })));
    });
  }, []);

  const handleLoadFromGroup = async () => {
    if (!selectedGroupId) return;
    setLoadingGroup(true);
    try {
      const group = await pb.collection('groups').getOne<Group>(selectedGroupId, {
        expand: 'usersIds',
        fields: 'id,usersIds,expand',
      });
      const expandedUsers = group.expand?.usersIds ?? [];
      const current = watch('members');
      const currentIds = new Set(current.map(m => m.userId));
      const toAdd = expandedUsers
        .filter(u => u.id && !currentIds.has(u.id))
        .map(u => ({ userId: u.id, userName: u.name || u.email || '', number: 0 }));
      toAdd.forEach(m => append(m));
    } catch {
      // ignore
    } finally {
      setLoadingGroup(false);
    }
  };

  const groupOptions: SelectOption[] = groups.map(g => ({ value: g.id, label: g.name || `Группа ${g.id.slice(-4)}` }));

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      // Пересоздаём участников: удаляем прежних и создаём актуальных — всё одним batch-запросом.
      await saveIntScorozvon({
        mode,
        id: initial?.id,
        data: { groupName: values.groupName, managerId: values.managerId },
        members: values.members.filter(m => m.userId).map(m => ({ userId: m.userId, number: m.number })),
        removeMemberIds: mode === 'edit' ? originalMemberIds : [],
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 560, overflow: 'visible' }}>
        <div className="cm-top">
          <div className="cm-ti">
            {mode === 'add' ? 'Создать запись' : 'Редактировать запись'}
          </div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 16, overflow: 'visible' }}>
          <div className="adm-field">
            <label className="adm-label">
              Название группы <span className="adm-req">*</span>
            </label>
            <div className="adm-input-wrap">
              <IconUsersGroup size={15} stroke={2} className="adm-input-icon" />
              <Input
                {...register('groupName', { required: 'Укажите название группы' })}
                className="adm-input"
                placeholder="Введите название..."
                disabled={mode === 'edit' && !isAdmin}
              />
            </div>
            {errors.groupName?.message && (
              <div className="form-field-error">{errors.groupName.message}</div>
            )}
          </div>

          <div className="adm-field">
            <label className="adm-label">Менеджер</label>
            <Controller
              control={control}
              name="managerId"
              render={({ field }) => {
                const nameField = watch('managerName');
                return (
                  <UserSearchInput
                    value={field.value}
                    selectedName={nameField}
                    onSelect={(id, name) => {
                      field.onChange(id);
                      setValue('managerName', name);
                    }}
                    onClear={() => {
                      field.onChange('');
                      setValue('managerName', '');
                    }}
                    placeholder="Выберите менеджера..."
                    disabled={mode === 'edit' && !isAdmin}
                  />
                );
              }}
            />
          </div>

          <div className="adm-field">
            <label className="adm-label" style={{ marginBottom: 8 }}>
              Пользователи
              {fields.length > 0 && (
                <span className="adm-hint"> — {fields.length} чел.</span>
              )}
            </label>

            <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fields.map((field, index) => (
              <div key={field.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <Controller
                    control={control}
                    name={`members.${index}.userId`}
                    rules={{
                      validate: id => {
                        if (!id) return true;
                        const members = watch('members');
                        const isDupe = members.some((m, i) => i !== index && m.userId === id);
                        return isDupe ? 'Пользователь уже добавлен' : true;
                      },
                    }}
                    render={({ field: f, fieldState }) => (
                      <>
                        <UserSearchInput
                          value={f.value}
                          selectedName={field.userName}
                          onSelect={(id, name) => {
                            f.onChange(id);
                            setValue(`members.${index}.userName`, name);
                          }}
                          onClear={() => {
                            f.onChange('');
                            setValue(`members.${index}.userName`, '');
                          }}
                          placeholder="Выберите пользователя..."
                          portal
                        />
                        {fieldState.error && (
                          <div className="form-field-error">{fieldState.error.message}</div>
                        )}
                      </>
                    )}
                  />
                </div>
                <div style={{ width: 80 }}>
                  <Input
                    {...register(`members.${index}.number`, { valueAsNumber: true })}
                    type="number"
                    placeholder="Кол-во"
                    min={0}
                    autoComplete="off"
                    className="adm-input"
                    style={{ padding: '0 8px' }}
                  />
                </div>
                <button
                  type="button"
                  className="cm-x"
                  style={{ flexShrink: 0 }}
                  onClick={() => remove(index)}
                >
                  <IconTrash size={14} stroke={2} />
                </button>
              </div>
            ))}
            </div>

            <Button
              variant="secondary"
              type="button"
              style={{ marginTop: 4 }}
              onClick={() => append({ userId: '', userName: '', number: 0 })}
            >
              <IconPlus size={14} stroke={2.5} />
              Добавить пользователя
            </Button>

            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <Select
                  options={groupOptions}
                  value={selectedGroupId}
                  onChange={setSelectedGroupId}
                  placeholder="Выбрать группу..."
                  searchable
                />
              </div>
              <Button
                variant="secondary"
                type="button"
                disabled={!selectedGroupId || loadingGroup}
                onClick={handleLoadFromGroup}
              >
                {loadingGroup ? <span className="adm-btn-spinner" /> : null}
                Заполнить из группы
              </Button>
            </div>
          </div>

          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { IntScorozvonModal };
